import React, { useEffect } from "react";
import { LayoutWrapper } from "../../../components/layoutWrapper";
import useStyles from "../../../components/util/styles";
import {
  Container,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Clear as ClearIcon, Search as SearchIcon } from "@material-ui/icons";
import { User } from "../../../context/authContext/reducer";
import { getAllUsers } from "../../../lib/adminRequests";
import { useHistory } from "react-router-dom";
import { rowsPerPageOptions, userTableHeadCells } from "../../../constants";
import { formatDate } from "../../../utils/admin";
import { useQuery } from "../../../hooks/useQuery";
import Colors from "../../../components/util/colors";
import { Alert } from "@material-ui/lab";

export const Users = () => {
  const classes = useStyles();
  const [users, setUsers] = React.useState([] as User[]);
  const history = useHistory();
  const { search, pathname } = useQuery();

  const pageNumber = Number(search.get("page"));
  const pageSize = Number(search.get("pageSize"));

  const initialPage = pageNumber > 0 ? pageNumber : 0;
  const initialRowsPerPage = rowsPerPageOptions.includes(pageSize)
    ? pageSize
    : 25;

  const [page, setPage] = React.useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
  const [searchTerm, setSearchTerm] = React.useState(search.get("q") || "");
  const [initialError, setInitialError] = React.useState<string>("");

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTerm(event.target.value);
    // Results shrink as you type, so an old page offset can land out of range.
    setPage(0);
  };

  const filteredUsers = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) =>
      [user.email, user.name, user.last_name, user.id, user.customer_id].some(
        (field) => field && field.toLowerCase().includes(term)
      )
    );
  }, [users, searchTerm]);

  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        const usersResponse = await getAllUsers();
        setUsers(usersResponse.users);
      } catch (err: any) {
        setInitialError(err);
      }
    };
    loadAllUsers();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(rowsPerPage),
    });
    const term = searchTerm.trim();
    if (term) params.set("q", term);

    history.replace(`${pathname}?${params.toString()}`);
  }, [pathname, page, rowsPerPage, searchTerm, history]);

  return (
    <LayoutWrapper pathName="admin/users">
      <Container className={classes.container} maxWidth="xl">
        {initialError.length ? (
          <Alert severity="error">{initialError}</Alert>
        ) : (
          <div style={{ height: "auto", width: "100%" }}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search by email, name, user ID or Stripe ID"
              value={searchTerm}
              onChange={handleChangeSearch}
              style={{ marginBottom: 16 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={() => {
                        setSearchTerm("");
                        setPage(0);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Table size="medium">
              <TableHead>
                <TableRow>
                  {userTableHeadCells.map((cell) => (
                    <TableCell
                      key={cell}
                      style={{
                        backgroundColor: Colors.vinousRed,
                        color: "white",
                      }}
                    >
                      <Typography style={{ fontWeight: "bold" }}>
                        {cell}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={userTableHeadCells.length}>
                      <Typography>
                        {users.length === 0
                          ? "Loading users…"
                          : `No users match "${searchTerm.trim()}".`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow
                        key={user.id}
                        hover
                        onClick={() => history.push(`user/${user.id}`)}
                      >
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.last_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.admin_role}</TableCell>
                        <TableCell>{user.customer_id}</TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              rowsPerPageOptions={rowsPerPageOptions}
              colSpan={3}
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  "aria-label": "rows per page",
                },
                native: true,
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        )}
      </Container>
    </LayoutWrapper>
  );
};
